import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ProgressBar } from "./skeletons";

/**
 * PageTransition component for smooth transitions between pages
 */
export function PageTransition({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [displayLocation, setDisplayLocation] = useState(location);

  useEffect(() => {
    if (location !== displayLocation) {
      setIsTransitioning(true);
      setProgress(0);

      // Simulate progressive loading
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 50);

      // Complete transition after a short delay
      const transitionTimer = setTimeout(() => {
        setProgress(100);
        setDisplayLocation(location);
        
        setTimeout(() => {
          setIsTransitioning(false);
          setProgress(0);
        }, 100);
      }, 50);

      return () => {
        clearInterval(progressInterval);
        clearTimeout(transitionTimer);
      };
    }
  }, [location, displayLocation]);

  return (
    <>
      {isTransitioning && <ProgressBar progress={progress} />}
      <div
        className={`transition-opacity duration-200 ${
          isTransitioning ? "opacity-80" : "opacity-100"
        }`}
      >
        {children}
      </div>
    </>
  );
}

/**
 * FadeTransition component for fade in/out effects
 */
export function FadeTransition({ 
  children, 
  show = true,
  duration = 300 
}: { 
  children: React.ReactNode;
  show?: boolean;
  duration?: number;
}) {
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  if (!shouldRender) return null;

  return (
    <div
      className="transition-opacity"
      style={{
        opacity: show ? 1 : 0,
        transitionDuration: `${duration}ms`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * SlideTransition component for slide in/out effects
 */
export function SlideTransition({
  children,
  show = true,
  direction = "right",
  duration = 300,
}: {
  children: React.ReactNode;
  show?: boolean;
  direction?: "left" | "right" | "up" | "down";
  duration?: number;
}) {
  const [shouldRender, setShouldRender] = useState(show);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration]);

  if (!shouldRender) return null;

  const getTransform = () => {
    if (!show) {
      switch (direction) {
        case "left":
          return "translateX(-100%)";
        case "right":
          return "translateX(100%)";
        case "up":
          return "translateY(-100%)";
        case "down":
          return "translateY(100%)";
      }
    }
    return "translate(0, 0)";
  };

  return (
    <div
      className="transition-transform"
      style={{
        transform: getTransform(),
        transitionDuration: `${duration}ms`,
      }}
    >
      {children}
    </div>
  );
}
